import { supabase } from '../../config/supabase';
import { Agency, isValidLogoFormat, DEFAULT_LOGO_CONFIG } from '../../types/agency';
import { compressImage, validateImageFile } from '../../utils/imageCompression';

/**
 * Service pour la gestion des agences et leurs logos
 */
export class AgencyService {
  /**
   * Convertit une image base64 en File object
   */
  static base64ToFile(base64: string, filename: string, mimeType: string): File {
    const byteCharacters = atob(base64.split(',')[1]);
    const byteNumbers = new Array(byteCharacters.length);
    
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: mimeType });
    
    return new File([blob], filename, { type: mimeType });
  }
  /**
   * Récupérer les informations de l'agence avec son logo
   */
  static async getAgencyWithLogo(agencyId: string): Promise<Agency | null> {
    try {
      const { data, error } = await supabase
        .from('agencies')
        .select('id, name, address, logo_url, created_at')
        .eq('id', agencyId)
        .single();

      if (error) {
        console.error('Erreur lors de la récupération de l\'agence:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Erreur inattendue:', error);
      return null;
    }
  }

  /**
   * Mettre à jour le logo de l'agence
   */
  static async updateAgencyLogo(agencyId: string, logoUrl: string | null): Promise<boolean> {
    try {
      const { error } = await supabase.rpc('update_agency_logo', {
        agency_id_param: agencyId,
        logo_url_param: logoUrl
      });

      if (error) {
        console.error('Erreur lors de la mise à jour du logo:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Erreur inattendue:', error);
      return false;
    }
  }

  /**
   * Uploader une photo de profil directement en base64 dans la table users (méthode simple)
   */
  static async uploadPhotoDirect(userId: string, file: File): Promise<string | null> {
    try {
      // Validation du format avec la fonction existante
      const validation = validateImageFile(file);
      if (!validation.isValid) {
        console.error('❌ Validation échouée:', validation.error);
        throw new Error(validation.error);
      }

      // Validation supplémentaire pour les formats spécifiques aux photos
      if (!isValidLogoFormat(file.name)) {
        console.error('❌ Format photo non supporté:', file.name);
        throw new Error(`Format de fichier non supporté. Formats acceptés: ${DEFAULT_LOGO_CONFIG.allowedFormats.join(', ')}`);
      }

      const processedFile = file;

      // Compresser l'image si c'est un format bitmap (pas SVG)
      if (file.type.startsWith('image/') && !file.type.includes('svg')) {
        try {
          // Utiliser la fonction de compression existante avec des paramètres optimisés pour les avatars
          const compressedBase64 = await compressImage(file, {
            maxWidth: 400,    // 2x la taille d'affichage pour bonne qualité (200px x 2)
            maxHeight: 400,   // 2x la taille d'affichage pour bonne qualité (200px x 2)
            quality: 0.9,     // Haute qualité pour les photos de profil
            maxSizeKB: 100    // 100KB max pour stockage optimal
          });
          
          // Mettre à jour directement la table users avec le base64
          const { data, error } = await supabase
            .from('users')
            .update({ 
              photo_url: compressedBase64
            })
            .eq('id', userId)
            .select('photo_url')
            .single();

          if (error) {
            console.error('❌ Erreur mise à jour base de données:', error);
            throw error;
          }

          return compressedBase64;
          
        } catch (compressionError) {
          console.warn('⚠️ Compression échouée, utilisation du fichier original:', compressionError);
          
          // Fallback : convertir le fichier original en base64 sans compression
          const reader = new FileReader();
          const base64 = await new Promise<string>((resolve, reject) => {
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(file);
          });

          const { data, error } = await supabase
            .from('users')
            .update({ 
              photo_url: base64
            })
            .eq('id', userId)
            .select('photo_url')
            .single();

          if (error) {
            console.error('❌ Erreur mise à jour base de données (fallback):', error);
            throw error;
          }

          return base64;
        }
      }

      return null;
    } catch (error) {
      console.error('💥 Erreur upload photo direct:', error);
      return null;
    }
  }

  /**
   * Supprimer la photo de profil de l'utilisateur (méthode simple directe)
   */
  static async removeUserPhoto(userId: string): Promise<boolean> {
    try {
      // Supprimer directement le photo_url de la table users
      const { error } = await supabase
        .from('users')
        .update({ 
          photo_url: null
        })
        .eq('id', userId);

      if (error) {
        console.error('❌ Erreur suppression photo en base:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Erreur lors de la suppression de la photo:', error);
      return false;
    }
  }

  /**
   * Uploader un fichier logo directement en base64 dans la table agencies (solution simple)
   */
  static async uploadLogoDirect(agencyId: string, file: File): Promise<string | null> {
    try {
      // Validation du format avec la fonction existante
      const validation = validateImageFile(file);
      if (!validation.isValid) {
        console.error('❌ Validation échouée:', validation.error);
        throw new Error(validation.error);
      }

      // Validation supplémentaire pour les formats spécifiques aux logos
      if (!isValidLogoFormat(file.name)) {
        console.error('❌ Format logo non supporté:', file.name);
        throw new Error(`Format de fichier non supporté. Formats acceptés: ${DEFAULT_LOGO_CONFIG.allowedFormats.join(', ')}`);
      }

      const processedFile = file;

      // Compresser l'image si c'est un format bitmap (pas SVG)
      if (file.type.startsWith('image/') && !file.type.includes('svg')) {
        try {
          // Utiliser la fonction de compression existante avec des paramètres optimisés pour les logos
          const compressedBase64 = await compressImage(file, {
            maxWidth: 96,     // 2x la taille d'affichage pour bonne qualité (48px x 2)
            maxHeight: 48,    // 2x la taille d'affichage pour bonne qualité (24px x 2)
            quality: 0.9,     // Haute qualité pour les petits logos
            maxSizeKB: 20     // Taille optimale pour stockage
          });
          
          // Mettre à jour directement la table agencies avec le base64
          const { data, error } = await supabase
            .from('agencies')
            .update({ 
              logo_url: compressedBase64
            })
            .eq('id', agencyId)
            .select('logo_url')
            .single();

          if (error) {
            console.error('❌ Erreur mise à jour base de données:', error);
            throw error;
          }

          return compressedBase64;
          
        } catch (compressionError) {
          console.warn('⚠️ Compression échouée, utilisation du fichier original:', compressionError);
          
          // Fallback : convertir le fichier original en base64 sans compression
          const reader = new FileReader();
          const base64 = await new Promise<string>((resolve, reject) => {
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(file);
          });

          const { data, error } = await supabase
            .from('agencies')
            .update({ 
              logo_url: base64
            })
            .eq('id', agencyId)
            .select('logo_url')
            .single();

          if (error) {
            console.error('❌ Erreur mise à jour base de données (fallback):', error);
            throw error;
          }

          return base64;
        }
      }

      return null;
    } catch (error) {
      console.error('💥 Erreur upload logo direct:', error);
      return null;
    }
  }

  /**
   * Uploader un fichier logo dans Supabase Storage avec compression automatique (méthode backup)
   */
  static async uploadLogoFile(agencyId: string, file: File): Promise<string | null> {
    try {
      console.log('🚀 Début upload logo:', { agencyId, fileName: file.name, fileSize: file.size });

      // Validation du format avec la fonction existante
      const validation = validateImageFile(file);
      if (!validation.isValid) {
        console.error('❌ Validation échouée:', validation.error);
        throw new Error(validation.error);
      }

      // Validation supplémentaire pour les formats spécifiques aux logos
      if (!isValidLogoFormat(file.name)) {
        console.error('❌ Format logo non supporté:', file.name);
        throw new Error(`Format de fichier non supporté. Formats acceptés: ${DEFAULT_LOGO_CONFIG.allowedFormats.join(', ')}`);
      }

      let processedFile = file;

      // Compresser l'image si c'est un format bitmap (pas SVG)
      if (file.type.startsWith('image/') && !file.type.includes('svg')) {
        try {
          console.log(`📸 Compression du logo: ${Math.round(file.size / 1024)}KB original`);
          
          // Utiliser la fonction de compression existante avec des paramètres optimisés pour les logos
          const compressedBase64 = await compressImage(file, {
            maxWidth: 800,    // Taille maximale pour les logos
            maxHeight: 800,   // Ratio carré supporté
            quality: 0.9,     // Haute qualité pour les logos
            maxSizeKB: 1024   // 1MB max après compression
          });

          // Convertir le base64 compressé en File
          processedFile = this.base64ToFile(compressedBase64, file.name, file.type);
          console.log(`✅ Logo compressé: ${Math.round(processedFile.size / 1024)}KB final`);
          
        } catch (compressionError) {
          console.warn('⚠️ Compression échouée, utilisation du fichier original:', compressionError);
          // Continuer avec le fichier original si la compression échoue
        }
      }

      // Générer un nom de fichier unique
      const fileExt = file.name.split('.').pop()?.toLowerCase();
      const fileName = `${agencyId}/logo.${fileExt}`;
      const filePath = `agency-logos/${fileName}`;

      console.log('📁 Chemin du fichier:', filePath);
      console.log('📊 Fichier final:', {
        name: processedFile.name,
        size: processedFile.size,
        type: processedFile.type
      });

      // Uploader le fichier dans Supabase Storage
      console.log('⬆️ Tentative d\'upload dans Supabase Storage...');
      const { data, error } = await supabase.storage
        .from('agency-assets')
        .upload(filePath, processedFile, {
          cacheControl: '3600',
          upsert: true
        });

      if (error) {
        console.error('❌ Erreur Supabase Storage:', {
          message: error.message,
          statusCode: (error as any).statusCode || 'unknown',
          error: (error as any).error || 'unknown'
        });
        
        // Analyse détaillée de l'erreur
        if (error.message?.includes('row-level security policy')) {
          console.error('🔒 Problème RLS: Vérifiez les politiques du bucket agency-assets');
          console.error('🔒 Vérifiez que: 1) Target roles = authenticated, 2) Bucket name = agency-assets');
        }
        
        throw error;
      }

      console.log('✅ Upload réussi:', data);

      // Récupérer l'URL publique du fichier
      const { data: { publicUrl } } = supabase.storage
        .from('agency-assets')
        .getPublicUrl(filePath);

      console.log('🔗 URL publique générée:', publicUrl);
      return publicUrl;
    } catch (error) {
      console.error('💥 Erreur complète lors de l\'upload du logo:', error);
      return null;
    }
  }

  /**
   * Supprimer le logo de l'agence (méthode simple directe)
   */
  static async removeAgencyLogo(agencyId: string): Promise<boolean> {
    try {
      console.log('🗑️ Suppression logo direct de la base de données...');
      
      // Supprimer directement le logo_url de la table agencies
      const { error } = await supabase
        .from('agencies')
        .update({ 
          logo_url: null
        })
        .eq('id', agencyId);

      if (error) {
        console.error('❌ Erreur suppression logo en base:', error);
        return false;
      }

      console.log('✅ Logo supprimé avec succès de la base de données');
      return true;
    } catch (error) {
      console.error('Erreur lors de la suppression du logo:', error);
      return false;
    }
  }

  /**
   * Mettre à jour complètement les informations de l'agence
   */
  static async updateAgency(agencyId: string, updates: Partial<Agency>): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('agencies')
        .update(updates)
        .eq('id', agencyId);

      if (error) {
        console.error('Erreur lors de la mise à jour de l\'agence:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Erreur inattendue:', error);
      return false;
    }
  }

  /**
   * Valider un fichier logo avant upload
   */
  static validateLogoFile(file: File): { isValid: boolean; error?: string } {
    // Utiliser la validation d'image existante
    const imageValidation = validateImageFile(file);
    if (!imageValidation.isValid) {
      return imageValidation;
    }

    // Validation supplémentaire pour les formats spécifiques aux logos
    if (!isValidLogoFormat(file.name)) {
      return {
        isValid: false,
        error: `Format de fichier non supporté pour les logos. Formats acceptés: ${DEFAULT_LOGO_CONFIG.allowedFormats.join(', ')}`
      };
    }

    // Validation de taille plus permissive car on va compresser
    if (file.size > 20 * 1024 * 1024) { // 20MB max avant compression
      return {
        isValid: false,
        error: `Fichier trop volumineux. Taille maximale: 20MB (le fichier sera compressé automatiquement)`
      };
    }

    return { isValid: true };
  }
}
